/**
 * The worklist row drawer — the acting surface. Answers three questions
 * without leaving the page: what is wrong (problem meaning), what do I do
 * (action), which field do I fix (raw vendor values under the matrix). Plus
 * one-click VIN copy, flag history across recent runs, the "mark worked"
 * memory, and notes — an operator's own words about the car, kept per VIN so
 * they outlive the run and the flag. Visual spec: the approved audit-remodel mockup artifact `.drawer`.
 */
import React, { useEffect, useState } from "react"

import {
  getProblemHistory,
  getVehicleOtherGroups,
  ProblemHistoryEntry,
  ProblemTypeInfo,
  VehicleResponse,
} from "../../../api/audit"
import { THEME } from "../../../config/theme"
import { formatProblemDetail, isReversibleDeparture } from "../../../utils/formatAge"
import { parseUtcTimestamp } from "../../../utils/parseUtcTimestamp"
import { useIsMobile } from "../../../hooks/useIsMobile"
import VendorMatrix from "../VendorMatrix"
import VehicleNotes from "./VehicleNotes"

interface Props {
  vehicle: VehicleResponse
  info: ProblemTypeInfo | null
  dealerGroupId: number
  /** The run being viewed — the other-groups lookup is per run. */
  runId?: number
  /** Registry lookup so a secondary flag reads as its label, not its slug. */
  labelFor?: (slug: string) => string
  worked: boolean
  onToggleWorked: (vehicle: VehicleResponse, nextWorked: boolean) => void
  onClose: () => void
}

const SEVERITY_COLORS = THEME.problemDisplay.severityColors as Record<string, string>

function shortDate(iso: string): string {
  return parseUtcTimestamp(iso).toLocaleDateString([], { month: "short", day: "numeric" })
}

export default function RowDrawer({
  vehicle, info, dealerGroupId, runId, labelFor, worked, onToggleWorked, onClose,
}: Props) {
  const [history, setHistory] = useState<ProblemHistoryEntry[] | null>(null)
  const [otherGroups, setOtherGroups] = useState<string[]>([])
  const [historyOpen, setHistoryOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  // Full-width sheet on phones; fixed right drawer on desktop.
  const isMobile = useIsMobile()

  // Load this vin+problem's flag history when the drawer opens.
  useEffect(() => {
    if (!vehicle.vin || !vehicle.problem_type) return
    let cancelled = false
    ;(async () => {
      try {
        const response = await getProblemHistory(
          dealerGroupId, vehicle.vin as string, vehicle.problem_type as string,
        )
        if (!cancelled) setHistory(response.history)
      } catch {
        if (!cancelled) setHistory([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [dealerGroupId, vehicle.vin, vehicle.problem_type])

  const copyVin = async () => {
    if (!vehicle.vin) return
    await navigator.clipboard.writeText(vehicle.vin)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1500)
  }

  const vehicleName =
    [vehicle.year, vehicle.make, vehicle.model]
      .filter((part) => part !== null && `${part}`.trim() !== "")
      .join(" ") || "Vehicle"

  // Reset on every car, or the previous car's secondary flags linger on a
  // freshly-opened drawer while the fetch is in flight.
  useEffect(() => {
    setOtherGroups([])
    // History collapses again on every car — Ian: "even when navigating off
    // the drawer and back into the drawer it will close again until reopened".
    setHistoryOpen(false)
    if (runId === undefined) return
    let cancelled = false
    getVehicleOtherGroups(runId, vehicle.id)
      .then((response) => { if (!cancelled) setOtherGroups(response.groups) })
      .catch(() => undefined)
    return () => { cancelled = true }
  }, [runId, vehicle.id])

  const accent = SEVERITY_COLORS[info?.severity ?? "cleanup"] ?? THEME.colors.semantic.info

  const blockLabel: React.CSSProperties = {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: "0.9px",
    textTransform: "uppercase",
    color: THEME.colors.neutral.light.textSecondary,
    marginBottom: 6,
  }

  return (
    <div
      role="dialog"
      aria-label={`${vehicleName} details`}
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        height: "100vh",
        width: isMobile ? "100%" : 380,
        maxWidth: isMobile ? "100vw" : "90vw",
        background: THEME.colors.white,
        boxShadow: "-4px 0 24px rgba(13, 26, 66, 0.18)",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
      }}
    >
      <div style={{ background: THEME.colors.navy, color: THEME.colors.white, padding: "14px 18px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{vehicleName}</div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close details"
            style={{
              background: "none", border: "none", color: THEME.colors.white,
              fontSize: 16, cursor: "pointer", padding: 0, lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        <div style={{ fontSize: 12, opacity: 0.75, display: "flex", gap: 10, flexWrap: "wrap", marginTop: 2 }}>
          <span style={{ fontFamily: THEME.fonts.mono }}>{vehicle.vin ?? "—"}</span>
          <span>Stock {vehicle.stock ?? "—"}</span>
          <span>{vehicle.rooftop ?? vehicle.tekion_rooftop ?? "—"}</span>
          {vehicle.age_days !== null && <span>{vehicle.age_days} days</span>}
          {vehicle.site_label && <span>site says: {vehicle.site_label}</span>}
        </div>
      </div>

      <div style={{ padding: "16px 18px", display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <div style={blockLabel}>Problem</div>
          <div style={{ fontSize: 13 }}>
            <span
              style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                background: THEME.colors.white,
                border: `1px solid ${THEME.colors.neutral.light.border}`,
                borderRadius: THEME.borderRadius.sm,
                padding: "4px 10px 4px 8px", fontWeight: 500, marginRight: 8,
              }}
            >
              <span style={{ width: 3, height: 14, borderRadius: 2, background: accent }} />
              {info?.label ?? vehicle.problem_type}
            </span>
            {info?.meaning}
          </div>
          {otherGroups.length > 0 && (
            // A car can be true in more than one group at once — a duplicate
            // whose two records each have their own problem, or a car with both
            // an inventory and a website finding. Naming the others here stops
            // an operator fixing one and assuming the car is clear.
            <div
              style={{
                fontSize: 12.5,
                marginTop: 6,
                color: THEME.colors.neutral.light.textSecondary,
              }}
            >
              Also flagged as{" "}
              {otherGroups.map((slug, i) => (
                <React.Fragment key={slug}>
                  {i > 0 && ", "}
                  <span style={{ fontWeight: 500, color: THEME.colors.neutral.light.textPrimary }}>
                    {labelFor ? labelFor(slug) : slug}
                  </span>
                </React.Fragment>
              ))}
            </div>
          )}
          {vehicle.problem_detail && (
            <div style={{ fontSize: 12.5, marginTop: 6, fontWeight: 500 }}>
              This car: {formatProblemDetail(vehicle.problem_detail, vehicle.problem_date)}
            </div>
          )}
          {isReversibleDeparture(vehicle.problem_date) && (
            <div
              style={{
                fontSize: 12,
                marginTop: 4,
                color: THEME.colors.semantic.warning,
                fontWeight: 500,
              }}
            >
              Verify before removing — a sale this recent can still be reversed
              onto the same stock number.
            </div>
          )}
        </div>

        <div>
          <div style={blockLabel}>Vendor status</div>
          {/* Five cells outgrow a phone screen — scroll within the block. */}
          <div style={{ overflowX: "auto" }}>
          <VendorMatrix
            tkBucket={vehicle.tk_bucket}
            rrBucket={vehicle.rr_bucket}
            hnBucket={vehicle.hn_bucket}
            millBucket={vehicle.mill_bucket}
            tkRaw={vehicle.tekion_status}
            rrRaw={vehicle.rr_step}
            millRaw={vehicle.rr_recon_step}
            hnRaw={vehicle.homenet_state}
            stockType={vehicle.vehicle_type}
          />
          </div>
        </div>

        {info && (
          <div>
            <div style={blockLabel}>Recommended action</div>
            <div
              style={{
                background: THEME.problemDisplay.severitySoft.info,
                borderRadius: THEME.borderRadius.md,
                padding: "10px 14px",
                fontSize: 13,
              }}
            >
              {info.action}
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={copyVin}
            style={{
              fontSize: 12, padding: "5px 10px",
              border: `1px solid ${THEME.colors.neutral.light.border}`,
              borderRadius: THEME.borderRadius.sm,
              background: THEME.colors.white,
              color: THEME.colors.neutral.light.textPrimary,
              fontFamily: THEME.fonts.primary, cursor: "pointer",
            }}
          >
            {copied ? "Copied" : "Copy VIN"}
          </button>
          {vehicle.website_url && (
            <a
              href={vehicle.website_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: 12, padding: "5px 10px",
                border: `1px solid ${THEME.colors.neutral.light.border}`,
                borderRadius: THEME.borderRadius.sm,
                background: THEME.colors.white,
                color: THEME.colors.neutral.light.textPrimary,
                fontFamily: THEME.fonts.primary,
                textDecoration: "none",
              }}
            >
              View on website →
            </a>
          )}
        </div>

        <div>
          {/* Collapsed by default and RESET on every car (Ian) — the open
              state is keyed to nothing, so leaving the drawer and coming back
              closes it again. A list that grows one entry per audit would
              otherwise push the notes off the screen for a car worked daily. */}
          <button
            type="button"
            onClick={() => setHistoryOpen((open) => !open)}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              background: "none", border: "none", padding: 0,
              cursor: "pointer", fontFamily: THEME.fonts.primary,
            }}
            aria-expanded={historyOpen}
          >
            <span
              style={{
                color: THEME.colors.neutral.light.textSecondary,
                fontSize: 10, width: 10, display: "inline-block",
                transform: historyOpen ? "rotate(90deg)" : undefined,
              }}
            >
              {/* U+25B8, not U+25B6 — the latter is an emoji codepoint and iOS
                  substitutes Apple Color Emoji for it. Same caret as the
                  worklist group headers. */}
              ▸
            </span>
            <span style={blockLabel}>
              History{history !== null && history.length > 0 ? ` (${history.length})` : ""}
            </span>
          </button>
          {historyOpen && (
            <div style={{ fontSize: 12, color: THEME.colors.neutral.light.textSecondary, marginTop: 4 }}>
              {history === null ? (
                "Loading…"
              ) : history.length === 0 ? (
                "Not flagged in any recent run."
              ) : (
                history.map((entry) => (
                  <div key={entry.run_id} style={{ marginBottom: 3 }}>
                    <span style={{ fontVariantNumeric: "tabular-nums" }}>
                      {shortDate(entry.started_at)}
                    </span>
                    {" — "}
                    <span style={{ color: THEME.colors.neutral.light.textPrimary }}>
                      {entry.problem_types
                        .map((slug) => (labelFor ? labelFor(slug) : slug))
                        .join(", ")}
                    </span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <div style={{ borderTop: `1px solid ${THEME.colors.neutral.light.border}`, paddingTop: 4 }}>
          <VehicleNotes
            vin={vehicle.vin}
            dealerGroupId={dealerGroupId}
            problemType={vehicle.problem_type ?? vehicle.website_problem_type ?? null}
            labelFor={labelFor}
          />
        </div>

        <label
          style={{
            display: "flex", alignItems: "center", gap: 8, fontSize: 13,
            borderTop: `1px solid ${THEME.colors.neutral.light.border}`,
            paddingTop: 12, cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={worked}
            onChange={(event) => onToggleWorked(vehicle, event.target.checked)}
          />
          Mark worked — carries across runs; retires itself when the problem resolves
        </label>
      </div>
    </div>
  )
}
